/**
 * lib/cashUpPdf.ts
 * ------------------------------------------------------------------
 * A4 cash-up reconciliation sheet in the finance house style (mirrors
 * receiptPdf.ts / invoicePdf.ts: jsPDF, navy letterhead, SCHOOL config,
 * fmtPula). Produced from a closed FinanceReconciliation record so the
 * accounts office has a signed sheet to file.
 *
 *   buildCashUpPdf(rec, opt).save('cashup_day_2026-06-27.pdf')
 * ---------------------------------------------------------------- */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import {
  fmtPula, PAYMENT_METHOD_LABELS, type PaymentMethod, type Payment, type FinanceReconciliation,
} from '@/lib/financeCore';

export interface CashUpPdfOptions {
  logoDataUrl?: string | null;
  periodLabel: string;
  receipts: Payment[];
}

const METHOD_ORDER: PaymentMethod[] = ['cash', 'eft', 'card', 'mobile', 'other'];

export function buildCashUpPdf(rec: FinanceReconciliation, opt: CashUpPdfOptions): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX; const bottom = 285;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  // ---- letterhead ----
  if (opt.logoDataUrl) {
    try { pdf.addImage(opt.logoDataUrl, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ }
  }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  // ---- title + meta ----
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('CASH-UP SHEET', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
  pdf.text(opt.periodLabel, right, y - 3.5, { align: 'right' });
  pdf.text(`Ref ${rec.id}`, right, y + 0.5, { align: 'right' });
  y += 6;
  if (rec.status === 'reopened') {
    setT([180, 83, 9]); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
    pdf.text('REOPENED — not a final close', MX, y); y += 4;
  }
  y += 4;

  // ---- totals by method (expected) ----
  setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
  pdf.text('RECEIPTS BY METHOD (SYSTEM)', MX, y); y += 5;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5);
  for (const m of METHOD_ORDER) {
    const amt = rec.systemTotals[m] || 0;
    setT(INK); pdf.text(PAYMENT_METHOD_LABELS[m], MX, y);
    pdf.text(fmtPula(amt), right, y, { align: 'right' });
    y += 6;
  }
  setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y - 2, right, y - 2);
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text('Total received', MX, y + 3);
  pdf.text(`${fmtPula(rec.grandTotal)}  ·  ${rec.receiptCount} receipts`, right, y + 3, { align: 'right' });
  y += 12;

  // ---- cash reconciliation box ----
  setF([240, 244, 250]); setD(NAVY); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 26, 'FD');
  setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
  pdf.text('CASH RECONCILIATION', MX + 3, y + 5);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5); setT(INK);
  pdf.text('Expected (system)', MX + 3, y + 12); pdf.text(fmtPula(rec.systemTotals.cash), MX + 70, y + 12, { align: 'right' });
  pdf.text('Counted / banked', MX + 3, y + 18); pdf.text(fmtPula(rec.counted.cash), MX + 70, y + 18, { align: 'right' });
  const balanced = rec.variance.cash === 0;
  setT(balanced ? [4, 120, 87] : [180, 83, 9]); pdf.setFont('helvetica', 'bold');
  pdf.text('Variance', MX + 90, y + 15); pdf.text(fmtPula(rec.variance.cash), right - 3, y + 15, { align: 'right' });
  y += 31;

  // ---- meta lines ----
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5);
  const meta: Array<[string, string]> = [];
  if (rec.bankingRef) meta.push(['Banking reference', rec.bankingRef]);
  if (rec.varianceReason) meta.push(['Variance reason', rec.varianceReason]);
  if (rec.note) meta.push(['Note', rec.note]);
  meta.push(['Closed by', rec.reconciledByEmail || rec.reconciledBy]);
  meta.push(['Closed at', new Date(rec.reconciledAt).toLocaleString()]);
  for (const [k, v] of meta) {
    setT(MUTED); pdf.text(k, MX, y);
    setT(INK); pdf.text(pdf.splitTextToSize(String(v), right - MX - 50)[0] as string, MX + 50, y);
    y += 6;
  }
  y += 4;

  // ---- receipt listing ----
  const list = [...opt.receipts].sort((a, b) => (a.receivedAt ?? '').localeCompare(b.receivedAt ?? ''));
  if (list.length) {
    setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
    pdf.text('RECEIPTS IN PERIOD', MX, y); y += 5;
    const head = () => {
      setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
      pdf.text('TIME', MX, y); pdf.text('RECEIPT', MX + 20, y); pdf.text('STUDENT', MX + 48, y);
      pdf.text('METHOD', MX + 120, y); pdf.text('AMOUNT', right, y, { align: 'right' });
      y += 3; setD(RULE); pdf.line(MX, y, right, y); y += 4;
    };
    head();
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(INK);
    for (const p of list) {
      if (y > bottom - 30) { pdf.addPage(); y = 18; head(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(INK); }
      const t = p.receivedAt ? new Date(p.receivedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
      pdf.text(t, MX, y);
      pdf.text(String(p.receiptNo ?? '—'), MX + 20, y);
      pdf.text((pdf.splitTextToSize(p.studentName ?? '—', 66)[0] as string), MX + 48, y);
      pdf.text(PAYMENT_METHOD_LABELS[p.method] ?? p.method, MX + 120, y);
      pdf.text(fmtPula(p.amount || 0), right, y, { align: 'right' });
      y += 5.5;
    }
  }

  // ---- signatures ----
  if (y > bottom - 24) { pdf.addPage(); y = 24; } else { y += 8; }
  setD(RULE); pdf.setLineWidth(0.3);
  pdf.line(MX, y, MX + 60, y); pdf.line(right - 60, y, right, y);
  setT(MUTED); pdf.setFontSize(8);
  pdf.text('Prepared by', MX, y + 4);
  pdf.text('Checked / authorised by', right - 60, y + 4);

  return pdf;
}
